import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaGraduationCap, FaArrowLeft } from "react-icons/fa";
import { toast } from "sonner";

import adminService from "../../../api/services/admin";
import { updateCourse } from "../../../store/slices/admin/asyncThunks";
import { useAdminModal } from "../../../contexts/AdminModalContext";
import { priceFormatter } from "../../../utils/utils";
import {
  SectionHeader,
  SectionTitle,
  BackButton,
  PageCard,
  PageCardBody,
  PageActions,
  LoadingContainer,
  ErrorState,
} from "../shared/DetailPage.styles";
import {
  Button,
  ModalGrid,
  CourseDetailsHeader,
  CourseDetailsIcon,
  CourseDetailsInfo,
  CourseDetailsTitle,
  CourseDetailsId,
  CourseInfoGrid,
  InfoField,
  InfoLabel,
  InfoValue,
  DescriptionSection,
  DescriptionValue,
  StatsGrid,
  StatCard,
  StatValue,
  StatLabel,
  CourseStatusBadge,
  EditForm,
  EditField,
  EditLabel,
  EditInput,
  EditTextArea,
} from "../AdminCourses/AdminCourses.styles";

const AdminCourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showConfirmModal } = useAdminModal();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const loadCourse = useCallback(() => {
    setLoading(true);
    setError(false);

    adminService
      .getCourseById(courseId)
      .then((response) => setCourse(response.course))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [courseId]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  const goBack = () => navigate("/admin-panel/kursevi");

  const startEdit = () => {
    setEditForm({
      title: course.title || "",
      category: course.category || "",
      price: course.price ?? "",
      discountPrice: course.discountPrice ?? "",
      shortDescription: course.shortDescription || "",
      description: course.description || "",
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      toast.error("Naziv kursa je obavezan");
      return;
    }
    const price = parseFloat(editForm.price);
    if (isNaN(price) || price <= 0) {
      toast.error("Cena mora biti broj veći od 0");
      return;
    }
    let discountPrice = null;
    if (editForm.discountPrice !== "" && editForm.discountPrice !== null) {
      discountPrice = parseFloat(editForm.discountPrice);
      if (isNaN(discountPrice) || discountPrice < 0) {
        toast.error("Akcijska cena mora biti broj veći ili jednak 0");
        return;
      }
      if (discountPrice >= price) {
        toast.error("Akcijska cena mora biti manja od redovne cene");
        return;
      }
    }

    setIsSaving(true);
    try {
      await dispatch(
        updateCourse({
          courseId: course.id,
          courseData: {
            title: editForm.title.trim(),
            category: editForm.category.trim() || null,
            price,
            discountPrice,
            shortDescription: editForm.shortDescription.trim() || null,
            description: editForm.description.trim() || null,
          },
        }),
      ).unwrap();

      toast.success("Kurs je uspešno izmenjen");
      setEditMode(false);
      loadCourse();
    } catch (err) {
      toast.error(err || "Greška pri izmeni kursa");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = () => {
    const willDeactivate = course.isActive;

    showConfirmModal(
      willDeactivate ? "Deaktivacija kursa" : "Aktivacija kursa",
      willDeactivate
        ? `Kurs "${course.title}" više neće biti vidljiv korisnicima na sajtu. Da li si sigurna?`
        : `Kurs "${course.title}" će ponovo biti vidljiv korisnicima na sajtu. Da li si sigurna?`,
      async () => {
        try {
          await dispatch(
            updateCourse({
              courseId: course.id,
              courseData: { isActive: !course.isActive },
            }),
          ).unwrap();

          toast.success(
            willDeactivate ? "Kurs je deaktiviran" : "Kurs je aktiviran",
          );
          loadCourse();
        } catch (err) {
          toast.error(err || "Greška pri izmeni statusa kursa");
        }
      },
    );
  };

  if (loading) {
    return (
      <LoadingContainer>
        <l-line-spinner size="40" stroke="3" speed="1" color="var(--accent)" />
      </LoadingContainer>
    );
  }

  if (error || !course) {
    return (
      <ErrorState>
        <h3>Kurs nije pronađen</h3>
        <p>Kurs ne postoji ili je u međuvremenu obrisan.</p>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad na kurseve
        </BackButton>
      </ErrorState>
    );
  }

  return (
    <>
      <SectionHeader>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad
        </BackButton>
        <SectionTitle>
          <FaGraduationCap />
          {editMode ? "Izmena kursa" : "Detalji kursa"}
        </SectionTitle>
      </SectionHeader>

      <PageCard>
        <PageCardBody>
          <ModalGrid>
            <CourseDetailsHeader>
              <CourseDetailsIcon>
                <FaGraduationCap />
              </CourseDetailsIcon>
              <CourseDetailsInfo>
                <CourseDetailsTitle>
                  {course.title}
                  {!course.isActive && (
                    <CourseStatusBadge $active={false}>
                      Neaktivan
                    </CourseStatusBadge>
                  )}
                </CourseDetailsTitle>
                <CourseDetailsId>ID: #{course.id.slice(0, 8)}</CourseDetailsId>
              </CourseDetailsInfo>
            </CourseDetailsHeader>

            {editMode ? (
              <EditForm>
                <EditField $full>
                  <EditLabel>Naziv kursa</EditLabel>
                  <EditInput
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm({ ...editForm, title: e.target.value })
                    }
                  />
                </EditField>
                <EditField>
                  <EditLabel>Kategorija</EditLabel>
                  <EditInput
                    value={editForm.category}
                    onChange={(e) =>
                      setEditForm({ ...editForm, category: e.target.value })
                    }
                  />
                </EditField>
                <EditField>
                  <EditLabel>Cena (RSD)</EditLabel>
                  <EditInput
                    type="number"
                    min="0"
                    value={editForm.price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, price: e.target.value })
                    }
                  />
                </EditField>
                <EditField>
                  <EditLabel>Akcijska cena (RSD, opciono)</EditLabel>
                  <EditInput
                    type="number"
                    min="0"
                    value={editForm.discountPrice}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        discountPrice: e.target.value,
                      })
                    }
                  />
                </EditField>
                <EditField>
                  <EditLabel>Kratak opis</EditLabel>
                  <EditInput
                    value={editForm.shortDescription}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        shortDescription: e.target.value,
                      })
                    }
                  />
                </EditField>
                <EditField $full>
                  <EditLabel>Opis</EditLabel>
                  <EditTextArea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                  />
                </EditField>
              </EditForm>
            ) : (
              <>
                <CourseInfoGrid>
                  <InfoField>
                    <InfoLabel>Kategorija</InfoLabel>
                    <InfoValue>
                      {course.category || "Nije definisana"}
                    </InfoValue>
                  </InfoField>
                </CourseInfoGrid>

                {course.description && (
                  <DescriptionSection>
                    <InfoLabel>Opis</InfoLabel>
                    <DescriptionValue>{course.description}</DescriptionValue>
                  </DescriptionSection>
                )}

                <StatsGrid>
                  <StatCard>
                    <StatValue>{course.totalEnrollments || 0}</StatValue>
                    <StatLabel>Ukupno studenata</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {priceFormatter(course.revenue || 0)} RSD
                    </StatValue>
                    <StatLabel>Ukupan prihod</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {priceFormatter(course.discountPrice || course.price)}{" "}
                      RSD
                    </StatValue>
                    <StatLabel>Cena</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {new Date(course.createdAt).toLocaleDateString(
                        "sr-latn-RS",
                      )}
                    </StatValue>
                    <StatLabel>Datum kreiranja</StatLabel>
                  </StatCard>
                </StatsGrid>
              </>
            )}
          </ModalGrid>
        </PageCardBody>

        <PageActions>
          {editMode ? (
            <>
              <Button
                $secondary
                onClick={() => setEditMode(false)}
                disabled={isSaving}
              >
                Otkaži
              </Button>
              <Button $primary onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? "Čuvam..." : "Sačuvaj"}
              </Button>
            </>
          ) : (
            <>
              <Button $secondary onClick={handleToggleActive}>
                {course.isActive ? "Deaktiviraj" : "Aktiviraj"}
              </Button>
              <Button $primary onClick={startEdit}>
                Izmeni
              </Button>
            </>
          )}
        </PageActions>
      </PageCard>
    </>
  );
};

export default AdminCourseDetails;
